import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, TrendingUp, Phone, Clock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/layout/Navigation";
import Footer from "@/components/layout/Footer";
import { cafeSalesData, stateInfo, formatCurrency } from "@/data/cafeSalesData";

const RecentSalesStatePage = () => {
    const { state } = useParams();
    const stateKey = state?.toUpperCase();
    
    // Validate state
    if (!stateKey || !cafeSalesData[stateKey]) {
        return (
            <div className="min-h-screen bg-white">
                <Navigation />
                <div className="max-w-7xl mx-auto px-4 py-20 text-center">
                    <h1 className="text-3xl font-bold text-gray-900 mb-4">State Not Found</h1>
                    <p className="text-gray-600 mb-8">The requested state could not be found.</p>
                    <Link to="/">
                        <Button>Return Home</Button>
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    const salesData = cafeSalesData[stateKey];
    const info = stateInfo[stateKey];
    
    // Calculate stats
    const totalSales = salesData.length;
    const totalValue = salesData.reduce((sum, sale) => sum + sale.salePrice, 0);
    const avgPrice = Math.round(totalValue / totalSales);
    const avgDays = Math.round(salesData.reduce((sum, sale) => sum + sale.daysOnMarket, 0) / totalSales);
    const avgBuyers = Math.round(salesData.reduce((sum, sale) => sum + sale.interestedBuyers, 0) / totalSales);

    return (
        <div className="min-h-screen bg-gray-50">
            <Navigation />
            
            {/* Hero Section with Background Image */}
            <section className="relative h-[300px] md:h-[400px] overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={info.heroImage}
                        alt={`Cafes in ${info.name}`}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-900/70" />
                </div>
                
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <Link 
                            to="/" 
                            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Home
                        </Link>
                        
                        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            Recent Cafe Sales in {info.name}
                        </h1>
                        <p className="text-lg text-white/80 max-w-2xl">
                            {info.description}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Stats Bar */}
            <section className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-center"
                        >
                            <p className="text-3xl font-bold text-primary">{totalSales}</p>
                            <p className="text-sm text-gray-500">Total Sales</p>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="text-center"
                        >
                            <p className="text-3xl font-bold text-primary">{formatCurrency(avgPrice)}</p>
                            <p className="text-sm text-gray-500">Average Sale Price</p>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="text-center"
                        >
                            <p className="text-3xl font-bold text-primary">{avgDays}</p>
                            <p className="text-sm text-gray-500">Avg Days on Market</p>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="text-center"
                        >
                            <p className="text-3xl font-bold text-primary">{avgBuyers}</p>
                            <p className="text-sm text-gray-500">Avg Interested Buyers</p>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                            className="text-center"
                        >
                            <p className="text-3xl font-bold text-primary">{formatCurrency(totalValue)}</p>
                            <p className="text-sm text-gray-500">Total Sales Value</p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Sales Table */}
            <section className="py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="bg-white rounded-2xl shadow-lg overflow-hidden"
                    >
                        {/* Desktop Table */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full" data-testid="sales-table">
                                <thead className="bg-gray-50 border-b">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                                            #
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                                            <div className="flex items-center gap-2">
                                                <TrendingUp className="w-4 h-4 text-primary" />
                                                Sale Price (excl. SAV)
                                            </div>
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                                            <div className="flex items-center gap-2">
                                                <Clock className="w-4 h-4 text-primary" />
                                                Days on Market
                                            </div>
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                                            <div className="flex items-center gap-2">
                                                <Users className="w-4 h-4 text-primary" />
                                                Interested Buyers
                                            </div>
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                                            State
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {salesData.map((sale, index) => (
                                        <motion.tr
                                            key={sale.id}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.02 * index }}
                                            className="hover:bg-gray-50 transition-colors"
                                        >
                                            <td className="px-6 py-4 text-gray-500">
                                                {index + 1}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-lg font-semibold text-gray-900">
                                                    {formatCurrency(sale.salePrice)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                                    sale.daysOnMarket <= 100 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : sale.daysOnMarket <= 200 
                                                        ? 'bg-yellow-100 text-yellow-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {sale.daysOnMarket} days
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-gray-900 font-medium">
                                                    {sale.interestedBuyers}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-gray-600">
                                                {stateKey}
                                            </td>
                                        </motion.tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Cards */}
                        <div className="md:hidden divide-y divide-gray-100">
                            {salesData.map((sale, index) => (
                                <motion.div
                                    key={sale.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.02 * index }}
                                    className="p-4"
                                >
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex items-center gap-3">
                                            <span className="text-gray-400 text-sm w-6">{index + 1}</span>
                                            <div>
                                                <p className="font-semibold text-gray-900">{formatCurrency(sale.salePrice)}</p>
                                                <p className="text-sm text-gray-500">{stateKey}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex justify-between text-sm text-gray-500 ml-9">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                            sale.daysOnMarket <= 100 
                                                ? 'bg-green-100 text-green-800' 
                                                : sale.daysOnMarket <= 200 
                                                ? 'bg-yellow-100 text-yellow-800'
                                                : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {sale.daysOnMarket} days
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Users className="w-3 h-3" />
                                            {sale.interestedBuyers} buyers
                                        </span>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-12 bg-gradient-to-br from-primary/5 to-secondary/5">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                            Thinking of Selling Your Café in {info.name}?
                        </h2>
                        <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                            Get a free, no-obligation valuation of your café. Our team of experts 
                            specialise in café sales across {info.name}.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/cafe-valuer">
                                <Button
                                    size="lg"
                                    className="bg-primary hover:bg-primary/90 text-white rounded-xl px-8 py-6 text-lg font-semibold w-full sm:w-auto"
                                >
                                    Get Free Valuation
                                </Button>
                            </Link>
                            <a href="tel:1800370240">
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="border-2 border-primary text-primary hover:bg-primary hover:text-white rounded-xl px-8 py-6 text-lg font-semibold w-full sm:w-auto"
                                >
                                    <Phone className="w-5 h-5 mr-2" />
                                    1800 370240
                                </Button>
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default RecentSalesStatePage;
